import Box from '@mui/material/Box';
import type { ReactNode } from 'react';
import { useEffect, useMemo } from 'react';

import type { PagedQueryArg, QueryWhereValue } from 'src/apiClients/queries';
import { DataTable } from 'src/components/data-table/DataTable';
import type {
    DataTableColDef,
    DataTableRow,
    DataTableSort,
    OnRowClick,
} from 'src/components/data-table/DataTable.types';

import { useStyles } from './EntityListPage.styles';
import { useEntityListQueryState } from './hooks/use-entity-list-query-state';

/**
 * Shape an infinite paged query hook (RTK Query) returns when wired with
 * `DEFAULT_PAGE_SIZE` and the standard `{ rows, total }` envelope. Pages can
 * narrow `TRaw` to their endpoint's row type.
 */
export interface PagedInfiniteQueryResult<TRaw> {
    data?: { pages: Array<{ rows: TRaw[]; total: number }> };
    isLoading: boolean;
    isFetching: boolean;
    isError?: boolean;
    hasNextPage?: boolean;
    fetchNextPage: () => unknown;
}

export type PagedInfiniteQueryHook<TRaw> = (arg: PagedQueryArg) => PagedInfiniteQueryResult<TRaw>;

export interface EntityListPageProps<TRaw> {
    automationId: string;
    /** RTK Query infinite hook, e.g. `useGetUsersInfiniteQuery`. */
    useQuery: PagedInfiniteQueryHook<TRaw>;
    /** Server-side columns to OR-match against the user's debounced search input. */
    searchColumns: readonly string[];
    /** Initial sort applied on first render. */
    initialSort: DataTableSort[];
    columns: DataTableColDef[];
    /** Projects a raw row from the endpoint into a DataTable row. */
    mapRow: (raw: TRaw) => DataTableRow;
    onRowClick?: OnRowClick;
    /** Static filter conditions always applied to every query (e.g. subscriptionId). */
    baseWhere?: Record<string, QueryWhereValue>;
    /** Top-level query params appended outside of `where` (e.g. tenantId). */
    baseExtraParams?: Record<string, string>;
    titleWhenNoResults: string;
    messageWhenNoResults: string;
    /** When the query errors, show these instead of the no-results copy. */
    titleWhenLoadError?: string;
    messageWhenLoadError?: string;
    /** When true, removes the reserved scrollbar gutter space. */
    disableScrollbarGutter?: boolean;
    /** When provided, rows become expandable with a chevron toggle. */
    renderExpandedRow?: (row: DataTableRow) => ReactNode;
    /** Notified whenever the server-reported total changes. */
    onTotalChange?: (total: number) => void;
}

/**
 * Page-level scaffold for any server-paged entity list: query state +
 * debounced search + DataTable wiring. Pages supply the endpoint hook,
 * columns, and per-row projection.
 */
export const EntityListPage = <TRaw,>({
    automationId,
    useQuery,
    searchColumns,
    initialSort,
    columns,
    mapRow,
    onRowClick,
    baseWhere,
    baseExtraParams,
    titleWhenNoResults,
    messageWhenNoResults,
    titleWhenLoadError,
    messageWhenLoadError,
    disableScrollbarGutter,
    renderExpandedRow,
    onTotalChange,
}: EntityListPageProps<TRaw>) => {
    const { classes } = useStyles();
    const { queryState, setQueryState, queryArg } = useEntityListQueryState(
        searchColumns,
        initialSort,
        baseWhere,
        baseExtraParams,
    );

    const { data, isLoading, isFetching, isError, fetchNextPage, hasNextPage } = useQuery(queryArg);

    const rows = useMemo<DataTableRow[]>(() => {
        if (!data) return [];
        return data.pages.flatMap((p) => p.rows.map(mapRow));
    }, [data, mapRow]);

    const total = data?.pages[0]?.total ?? 0;
    const isLoadingMore = isFetching && !isLoading && rows.length > 0;
    const errored = !!isError;

    useEffect(() => {
        if (data) onTotalChange?.(total);
    }, [data, total, onTotalChange]);

    return (
        <Box className={classes.root} automation-id={automationId}>
            <Box className={classes.content}>
                <DataTable
                    columns={columns}
                    rows={errored ? [] : rows}
                    isLoading={isLoading && !errored}
                    onRowClick={onRowClick}
                    queryState={queryState}
                    onQueryStateChange={setQueryState}
                    totalRows={total}
                    hasMore={!!hasNextPage}
                    isLoadingMore={isLoadingMore}
                    onLoadMore={fetchNextPage}
                    titleWhenNoResults={
                        errored && titleWhenLoadError ? titleWhenLoadError : titleWhenNoResults
                    }
                    messageWhenNoResults={
                        errored && messageWhenLoadError
                            ? messageWhenLoadError
                            : messageWhenNoResults
                    }
                    disableScrollbarGutter={disableScrollbarGutter}
                    renderExpandedRow={renderExpandedRow}
                />
            </Box>
        </Box>
    );
};
